import { useEffect, useState } from "react";
import { Scale, RefreshCw, Loader2, AlertTriangle, Users } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";

const fmt = (n) => (n == null ? "—" : Math.round(n).toLocaleString("es-CO"));
const fmtPct = (p) => (p == null ? "—" : `${p > 0 ? "+" : ""}${p.toFixed(2)}%`);
const MES = { "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun", "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic" };

function Cell({ v, sub, muted }) {
  return (
    <td className={`py-1.5 px-2 text-right align-top ${muted ? "text-cm-ink-soft" : ""}`}>
      <div className="font-medium tabular-nums">{fmt(v)}</div>
      {sub && <div className="text-[10px] text-cm-ink-soft">{sub}</div>}
    </td>
  );
}

function Diff({ d }) {
  if (!d || d.abs == null) return <td className="py-1.5 px-2 text-right text-cm-ink-soft">—</td>;
  const cls = d.supera_umbral ? "text-red-700 font-semibold" : Math.abs(d.pct || 0) < 0.5 ? "text-emerald-700" : "text-amber-700";
  return (
    <td className={`py-1.5 px-2 text-right align-top tabular-nums ${cls}`}>
      <div>{fmt(d.abs)}</div>
      <div className="text-[10px]">{fmtPct(d.pct)}</div>
    </td>
  );
}

export function ConciliacionMensual({ execId, refreshKey, fuente }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/tsol/conciliacion", { params: execId ? { exec_id: execId } : {} });
      setData(data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [execId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-white border border-cm-border rounded-lg p-5" data-testid="tsol-conciliacion">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="font-serif text-lg flex items-center gap-2"><Scale className="h-5 w-5" /> Conciliación mensual · Nutrimenti</h3>
        <div className="flex items-center gap-2 text-xs">
          {fuente && (
            <span className={`px-2 py-0.5 rounded border ${fuente.fuente_ventas === "factura" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`} data-testid="tsol-fuente-badge">
              Fuente Ventas: {fuente.fuente_ventas === "factura" ? "FACTURA (Factura/Get)" : "PEDIDO"}
              {fuente.fuente_ventas === "factura" && (fuente.aprobada ? " · aprobada" : " · PENDIENTE de aprobación (envío manual)")}
            </span>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="tsol-conciliacion-refresh">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Actualizar
          </Button>
        </div>
      </div>
      {fuente?.carga_facturas_en_curso && (
        <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 mb-2" data-testid="tsol-carga-facturas">
          Carga de facturas EN CURSO (snapshot {String(fuente.snapshot_en_curso || "").slice(0, 8)}) · IDs {fuente.scan?.start_id} → {fuente.scan?.last_id} · encontradas {fuente.scan?.found ?? 0} · reutilizadas {fuente.scan?.reused ?? 0} · re-consultadas {fuente.scan?.refetched ?? 0} · inexistentes {fuente.scan?.missing ?? 0}. La tarea de las 19:00 responderá «carga en curso».
        </p>
      )}
      {!data ? <p className="text-xs text-cm-ink-soft">{loading ? "Calculando…" : "Sin datos."}</p> : (
        <>
          <p className="text-[11px] text-cm-ink-soft mb-2">{data.nota} · Facturas en caché: {data.facturas_cache_total}
            {data.zip_generado?.zips?.length > 0 ? (
              <> · ZIP generado por mes: {data.zip_generado.zips.map((z) => <code key={z.execution_id} className="mr-1" data-testid={`conc-zip-gen-${z.alcance}`}>{z.zip_name} ({z.alcance})</code>)}</>
            ) : data.zip_generado && <> · ZIP generado: <code>{data.zip_generado.zip_name}</code> ({data.zip_generado.fuente_ventas}, {data.zip_generado.state})</>}
            {data.zip_enviado?.zips?.length > 0 ? (
              <> · ZIP enviado por mes: {data.zip_enviado.zips.map((z) => <code key={z.execution_id} className="mr-1">{z.zip_name} ({z.alcance})</code>)}</>
            ) : data.zip_enviado && <> · ZIP enviado: <code>{data.zip_enviado.zip_name}</code> ({data.zip_enviado.fuente_ventas})</>}
            {data.zip_generado?.nota && <> · {data.zip_generado.nota}</>}
          </p>
          {data.alertas?.length > 0 && (
            <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 mb-2" data-testid="tsol-conciliacion-alertas">
              <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />Diferencia ZIP neto vs Referencia CM supera ±{data.umbral_pct}%: {data.alertas.join(" · ")}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="tsol-conciliacion-table">
              <thead className="text-left text-cm-ink-soft border-b border-cm-border">
                <tr>
                  <th className="py-1 px-2">Mes</th>
                  <th className="py-1 px-2 text-right">ZIP ventas (Tipo 0)</th>
                  <th className="py-1 px-2 text-right">NC Tipo 1 (ZIP)</th>
                  <th className="py-1 px-2 text-right">ZIP neto</th>
                  <th className="py-1 px-2 text-right">ZIP neto − Ref. CM</th>
                  <th className="py-1 px-2 text-right">ZIP enviado</th>
                  <th className="py-1 px-2 text-right">Dashboard Nutrimenti</th>
                  <th className="py-1 px-2 text-right">Facturas ARCOS</th>
                  <th className="py-1 px-2 text-right">Referencia CM</th>
                  <th className="py-1 px-2 text-right">ZIP − Dashboard</th>
                  <th className="py-1 px-2 text-right">ZIP − Facturas</th>
                  <th className="py-1 px-2 text-right">Facturas − Ref. CM</th>
                  <th className="py-1 px-2 text-right">NC Nutrimenti</th>
                  <th className="py-1 px-2 text-right">(Fact − NC) − Ref. CM</th>
                </tr>
              </thead>
              <tbody>
                {data.filas.map((r) => (
                  <tr key={r.mes} className="border-b border-cm-border/60 align-top" data-testid={`tsol-conciliacion-row-${r.mes}`}>
                    <td className="py-1.5 px-2 font-semibold">{MES[r.mes.slice(5)] || r.mes} {r.mes.slice(0, 4)}</td>
                    <Cell v={r.zip_generado?.valor} sub={r.zip_generado && `${r.zip_generado.facturas} fact · ${r.zip_generado.pedidos} ped · ${fmt(r.zip_generado.unidades)} und`} />
                    <Cell v={r.zip_generado?.devoluciones} sub={r.zip_generado && `${r.zip_generado.notas_credito} NC · ${r.zip_generado.lineas_dev} líneas · ${fmt(r.zip_generado.unidades_dev)} und`} />
                    <td className="py-1.5 px-2 text-right align-top font-semibold tabular-nums" data-testid={`tsol-zip-neto-${r.mes}`}>{fmt(r.zip_neto)}</td>
                    <Diff d={r.dif_zip_neto_vs_referencia} />
                    <Cell v={r.zip_enviado?.valor} muted sub={r.zip_enviado && `${r.zip_enviado.facturas} fact · ${fmt(r.zip_enviado.unidades)} und`} />
                    <Cell v={r.dashboard?.valor} sub={r.dashboard && `${r.dashboard.pedidos} ped · ${fmt(r.dashboard.unidades)} und`} />
                    <Cell v={r.facturas_arcos?.valor} sub={r.facturas_arcos && `${r.facturas_arcos.facturas} fact · ${fmt(r.facturas_arcos.unidades)} und · ${r.facturas_arcos.anuladas} anul · ${r.facturas_arcos.directas_sin_pedido} directas`} />
                    <Cell v={r.referencia_cm} muted />
                    <Diff d={r.dif_zip_vs_dashboard} />
                    <Diff d={r.dif_zip_vs_facturas} />
                    <Diff d={r.dif_facturas_vs_referencia} />
                    <Cell v={r.nc_nutrimenti?.valor} muted sub={r.nc_nutrimenti && `${r.nc_nutrimenti.nc} NC · no transmitidas`} />
                    <Diff d={r.dif_facturas_neto_vs_referencia} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <details className="mt-2 text-[11px]">
            <summary className="cursor-pointer text-cm-ink-soft">Explicación de diferencias por mes</summary>
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              {data.filas.map((r) => r.explicacion.length > 0 && (
                <li key={r.mes}><b>{r.mes}</b>: {r.explicacion.join("; ")}</li>
              ))}
            </ul>
          </details>
          {data.anomalias_precio?.length > 0 && (
            <div className="mt-3 border border-red-200 bg-red-50 rounded p-2 text-xs" data-testid="tsol-anomalias-precio">
              <div className="font-semibold text-red-900 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Anomalías de precio en ARCOS (unitario facturado &gt; {data.anomalia_factor}× precio de lista) — se transmiten tal cual; CM debe corregir en ARCOS (NC + refactura)</div>
              <table className="mt-1 w-full text-[11px]">
                <thead className="text-left text-red-800"><tr><th className="pr-2">Mes</th><th className="pr-2">Factura</th><th className="pr-2">Fecha</th><th className="pr-2">Cliente</th><th className="pr-2">Producto</th><th className="pr-2 text-right">Cant</th><th className="pr-2 text-right">Unit. facturado</th><th className="pr-2 text-right">Precio lista máx</th><th className="pr-2 text-right">Exceso</th></tr></thead>
                <tbody>{data.anomalias_precio.map((a) => (
                  <tr key={`${a.factura_id}-${a.sku}`} data-testid={`tsol-anomalia-${a.factura_id}-${a.sku}`}>
                    <td className="pr-2">{a.mes}</td><td className="pr-2"><code>{a.factura}</code> ({a.factura_id})</td><td className="pr-2">{a.fecha}</td>
                    <td className="pr-2">{a.cliente_id} · {a.cliente}</td><td className="pr-2">{a.sku} · {a.producto}</td>
                    <td className="pr-2 text-right">{a.cantidad}</td><td className="pr-2 text-right font-semibold">{fmt(a.unitario_facturado)}</td>
                    <td className="pr-2 text-right">{fmt(a.precio_lista_max)}</td><td className="pr-2 text-right font-semibold">{fmt(a.exceso_estimado)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          <div className="mt-3 border border-orange-200 bg-orange-50 rounded p-2 text-xs" data-testid="tsol-clientes-sin-asignacion">
            <div className="font-semibold text-orange-900 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Clientes del paquete con datos de maestro faltantes en ARCOS ({data.clientes_sin_asignacion?.length || 0}) — nombre, tipo de negocio o ruta. Sus ventas SÍ se transmiten; el campo faltante va VACÍO (nunca 0 ni valores por defecto). Corregir en ARCOS: salen en el paquete de esa misma noche (maestros desde Cliente/Get en vivo).</div>
            {(data.clientes_sin_asignacion || []).length === 0 ? <p className="text-orange-800">Ninguno en la última generación.</p> : (
              <table className="mt-1 w-full text-[11px]">
                <thead className="text-left text-orange-800"><tr><th className="pr-2">Código</th><th className="pr-2">Nombre</th><th className="pr-2">Problema</th><th className="pr-2">Vendedor que factura</th><th className="pr-2 text-right">Valor neto</th></tr></thead>
                <tbody>{data.clientes_sin_asignacion.map((c) => (
                  <tr key={c.cliente_id} data-testid={`tsol-sin-asignacion-${c.cliente_id}`}>
                    <td className="pr-2"><code>{c.cliente_id}</code></td><td className="pr-2">{c.nombre || <span className="italic">(sin nombre)</span>}</td>
                    <td className="pr-2">{c.problemas.join(", ")}</td><td className="pr-2">{c.vendedor_factura.join(" / ") || "—"}</td>
                    <td className="pr-2 text-right tabular-nums">{fmt(c.valor_neto)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
          {data.sku_excluidos?.length > 0 && (
            <p className="mt-2 text-[11px] text-cm-ink-soft" data-testid="tsol-sku-excluidos">
              SKU bajo proveedor Nutrimenti EXCLUIDOS (no son producto): {data.sku_excluidos.map((x) => `${x.sku} · ${x.nombre}`).join("; ")} (env <code>TSOL_SKU_EXCLUIDOS</code>).
            </p>
          )}
          {data.clientes_nombre_factura?.length > 0 && (
            <div className="mt-3 border border-sky-200 bg-sky-50 rounded p-2 text-xs" data-testid="tsol-clientes-nombre-factura">
              <div className="font-semibold text-sky-900 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Clientes sin nombre en Cliente/Get incluidos con <u>nombre tomado de la factura</u> (ClienteTerceroNombre)</div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {data.clientes_nombre_factura.map((c) => (
                  <span key={c.cliente_id} className="px-2 py-0.5 rounded bg-white border border-sky-200 text-sky-900" data-testid={`tsol-cliente-nombre-factura-${c.cliente_id}`}>
                    <code>{c.cliente_id}</code> · {c.nombre} · {c.facturas} fact · {fmt(c.valor)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-3 border border-amber-200 bg-amber-50 rounded p-2 text-xs" data-testid="tsol-clientes-sin-nombre">
            <div className="font-semibold text-amber-900 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Clientes sin nombre en ARCOS ni en la factura (sus ventas SÍ se transmiten; salen en Clientes con el nombre vacío hasta que CM los corrija)</div>
            {data.clientes_sin_nombre.length === 0 ? <p className="text-amber-800">Ninguno en las facturas del rango.</p> : (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {data.clientes_sin_nombre.map((c) => (
                  <span key={c.cliente_id} className="px-2 py-0.5 rounded bg-white border border-amber-200 text-amber-900" data-testid={`tsol-cliente-sin-nombre-${c.cliente_id}`}>
                    <code>{c.cliente_id}</code> · {c.facturas} fact · {fmt(c.valor)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
